import config from '../config/config';
import { Options } from '../index';
import { localStorageKey, sessionStorageKey, urlParameter } from '../library/Constants';
import Cookie, { CookieKey, CookieLifetime } from '../library/Cookie';
import {
  decodeValue,
  mapTrackingDimensions,
  parseUrlParameters,
  TRACKING_ERRORS_PROPERTY
} from '../library/TrackingDimensions';
import Lead from '../models/Lead';
import { generateGUID, getUrlParameter, utils } from '../utils/Utils';
import countryMap from './countryMap';

export const GID_PREFIX = '001';
export const GID_REACTIVATION_PREFIX = '888';

/**
 * Sliding window for the first-touch attribution cookies (referrer, landing URL).
 * Kept equal to the LeadGID lifetime so the two never expire out of step.
 */
export const ATTRIBUTION_COOKIE_LIFETIME = CookieLifetime.ONE_HOUR;

/**
 * Marker written next to the attribution cookies. Its absence tells us the stored
 * referrer/landing URL predate the sliding-lifetime fix (legacy session cookies with no
 * expiry) and must not be adopted. Bump when the cookie semantics change again.
 */
export const ATTRIBUTION_COOKIE_VERSION = '2';

export interface AttributionValues {
  referrer?: string;
  landingUrl?: string;
}

class Session {
  private sessionStorage: any;
  private localStorage: any;
  public leadId: string;
  public visitorId: string;
  public gid: string;
  public options: Options;
  public lead: Lead;
  private locale: string;
  private visitorTimestamp: string;
  private reactivationGidValidity: number;
  private lastLeadGid: string;
  private lastLeadCreatedAt: number;
  private suspectedBot: string;

  private nowTimestamp: number = new Date().getTime();

  constructor(sessionStorage: any, localStorage: any, options: Options) {
    this.reactivationGidValidity = config.validityTime.reactivationGid;

    const { locale, visitorId } = options;

    this.options = options;
    this.sessionStorage = sessionStorage;
    this.localStorage = localStorage;
    this.locale = locale;
    this.suspectedBot = utils.isWebDriver() ? 'webdriver' : undefined;

    this.touchAttributionCookies();

    this.leadId = this.getLeadIdFromCookie() || this.createLeadIdFromLocale(this.locale);
    this.visitorId = visitorId || generateGUID();

    if (!this.isReactivation()) {
      this.gid = this.selectGID();
      this.sessionStorage.setItem(sessionStorageKey.SESSION_GID, this.gid);
    }

    this.lead = new Lead(this.gid, this.leadId, this.visitorId, this.options);

    this.visitorTimestamp = this.localStorage.getItem(localStorageKey.VISITOR_TIMESTAMP);
    this.setVisitor();
  }

  public activateGid(gid: string, timestamp: number): void {
    this.gid = gid;

    if (this.isReactivation()) {
      this.localStorage.setItem(localStorageKey.REACTIVATION_GID, this.gid);
      this.localStorage.setItem(localStorageKey.REACTIVATION_GID_GENERATED_AT, timestamp.toString());
    } else {
      this.sessionStorage.setItem(sessionStorageKey.SESSION_GID, this.gid);
    }

    this.lead.updateGid(gid);
  }

  public getSuspectedBot(): string {
    return this.suspectedBot;
  }

  public addSuspectedBotSignal(signal: string): void {
    this.suspectedBot = this.suspectedBot ? `${this.suspectedBot},${signal}` : signal;
  }

  private setVisitor(): void {
    this.localStorage.setItem(localStorageKey.VISITOR_TIMESTAMP, this.nowTimestamp.toString());
  }

  public setVisitorSession(): void {
    this.sessionStorage.setItem(sessionStorageKey.VISITOR_TIMESTAMP, this.nowTimestamp.toString());
  }

  public isReturningVisitor(): boolean {
    const isSameVisitorSession = !!this.sessionStorage.getItem(sessionStorageKey.VISITOR_TIMESTAMP);
    return !!this.visitorTimestamp && !isSameVisitorSession;
  }

  public getLeadCreationTimeFromLocalStorage(): string {
    return this.localStorage.getItem(localStorageKey.LEAD_CREATION_TIME);
  }

  public getGidFromLocalStorage(): string {
    return this.localStorage.getItem(localStorageKey.GID);
  }

  public getLeadIdFromLocalStorage(): string {
    return this.localStorage.getItem(localStorageKey.LEAD_ID);
  }

  public isReactivation(): boolean {
    const { upsert } = this.options;
    return upsert === 'no';
  }

  public createReactivationGid(): string {
    return `${GID_REACTIVATION_PREFIX}.${generateGUID()}`;
  }

  public isValid() {
    const hasGID = !!this.getGid();

    if (this.isNewSession()) {
      return hasGID && this.lead.hasRequiredFields();
    }
    return hasGID;
  }

  public setLastLeadGid(lastLeadGid: string): void {
    this.lastLeadGid = lastLeadGid;
  }

  public setLastLeadCreatedAt(lastLeadCreatedAt: number): void {
    this.lastLeadCreatedAt = lastLeadCreatedAt;
  }

  public getReactivationGidFromLocalStorage(): { gid: string; timestamp: number; usable: boolean } {
    const gid = this.localStorage.getItem(localStorageKey.REACTIVATION_GID);
    const timestamp = parseInt(this.localStorage.getItem(localStorageKey.REACTIVATION_GID_GENERATED_AT));
    return {
      gid,
      timestamp,
      usable: this.isGidUsable({ gid, timestamp })
    };
  }

  public getReactivationGidFromCps(): { gid: string; timestamp: number; usable: boolean } {
    const gid = this.lastLeadGid;
    const timestamp = this.lastLeadCreatedAt;
    return {
      gid,
      timestamp,
      usable: this.isGidUsable({ gid, timestamp })
    };
  }

  private isGidUsable({ gid, timestamp }: { gid: string; timestamp: number }): boolean {
    if (!this.isValidReactivationGid(gid)) {
      return false;
    }

    return this.isGidFresh(timestamp);
  }

  public isGidFresh(timestamp: number): boolean {
    return this.isTimestampFresh(timestamp);
  }

  public isReactivatedRecently(): boolean {
    const reactivationTime = parseInt(this.localStorage.getItem(localStorageKey.REACTIVATION_TIME));
    return this.isTimestampFresh(reactivationTime);
  }

  private isTimestampFresh(timestamp: number): boolean {
    if (isNaN(timestamp)) {
      return false;
    }

    return utils.isTimeWithinLimit(timestamp, this.reactivationGidValidity);
  }

  public selectGID(): string {
    const sessionGid = this.sessionStorage.getItem(sessionStorageKey.SESSION_GID);

    if (this.isValidGid(sessionGid)) {
      return sessionGid;
    }

    return this.getPreviousGidFromUrl() || this.getGidFromCookie() || this.createNewGid();
  }

  public getLeadId(): string {
    return this.leadId;
  }

  public getGid(): string {
    return this.gid;
  }

  public getVisitorId(): any {
    return this.visitorId;
  }

  public createNewGid(): string {
    return `${GID_PREFIX}.${generateGUID()}`;
  }

  public isNewSession() {
    return !this.getGidFromCookie();
  }

  public isValidLeadId(leadId: string): boolean {
    if (!leadId || typeof leadId !== 'string') {
      return false;
    }

    const match = leadId.match(/[A-Z]{3}-[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/i) || [];
    return match.length > 0 && match[0] === leadId;
  }

  public getLeadIdFromCookie(): string {
    const leadId = Cookie.get(CookieKey.LEAD_ID);
    return this.isValidLeadId(leadId) ? leadId : '';
  }

  public getGidFromCookie(): string {
    const gid = Cookie.get(CookieKey.GID);
    return this.isValidGid(gid) ? gid : '';
  }

  public createLeadIdFromLocale(locale = 'de_DE'): string {
    const isoCode = countryMap[locale];
    return `${isoCode}-${generateGUID()}`;
  }

  /**
   * First-touch attribution (referrer, landing URL) lives in cookies with a sliding
   * lifetime: every SessionLayer init and every successful lead submit keeps the stored
   * value and pushes the expiry forward. A visit after the window has closed starts a
   * new first touch.
   *
   * Historically these were browser-session cookies (no expiry). Android WebViews and
   * Chrome's session restore never end a "session", so a lead created weeks later still
   * carried the landing URL of the very first ad click.
   *
   * Exception (CJT-15985): a new click from the same partner replaces the first touch. When the
   * page carries a click ID, its utm_source equals the stored landing URL's, and the click ID
   * differs, landing URL and referrer are taken from the page. Otherwise the partner's
   * conversion API would receive the earlier click's ID for a lead attributed to the later
   * click. A different utm_source never overwrites: partners share in-app webviews, and an
   * unconditional overwrite would let one steal the other's leads.
   *
   * Migration: JS cannot read a cookie's expiry, so a version marker (`attr_v`) is written
   * together with the two cookies and expires with them. Stored referrer/landing URL
   * whose marker is missing or holds a different version are legacy; they are ignored
   * and replaced with the current page values instead of being extended for another hour.
   *
   * @param values explicit values to keep (e.g. from the in-memory lead at submit time);
   *               non-empty values win over the cookie, the cookie wins over the page.
   */
  public touchAttributionCookies(values: AttributionValues = {}): void {
    // Cookie.get() JSON-parses values, so '2' comes back as a number: compare as strings.
    const legacy = String(Cookie.get(CookieKey.ATTRIBUTION_VERSION)) !== ATTRIBUTION_COOKIE_VERSION;
    const ignoreCookie = legacy || this.isNewClickFromSamePartner();
    const referrer = this.pickAttributionValue(CookieKey.REFERRER, values.referrer, document.referrer, ignoreCookie);
    const landingUrl = this.pickAttributionValue(CookieKey.LANDING_URL, values.landingUrl, this.getUrl(), ignoreCookie);

    Cookie.set(CookieKey.REFERRER, referrer, ATTRIBUTION_COOKIE_LIFETIME);
    Cookie.set(CookieKey.LANDING_URL, landingUrl, ATTRIBUTION_COOKIE_LIFETIME);
    Cookie.set(CookieKey.ATTRIBUTION_VERSION, ATTRIBUTION_COOKIE_VERSION, ATTRIBUTION_COOKIE_LIFETIME);
  }

  /**
   * True when the page is a new click from the partner the stored landing URL came from.
   * Compares URL parameters only: the stored URL may be an Optimizely variant of the page.
   */
  private isNewClickFromSamePartner(): boolean {
    if (!Cookie.has(CookieKey.LANDING_URL)) {
      return false;
    }

    const stored = String(Cookie.get(CookieKey.LANDING_URL));
    const current = this.getUrl();
    const currentClickId = getClickId(current);
    const currentSource = getUtmSource(current);

    return (
      !!currentClickId &&
      !!currentSource &&
      currentSource === getUtmSource(stored) &&
      currentClickId !== getClickId(stored)
    );
  }

  private pickAttributionValue(
    key: CookieKey,
    explicit: string | undefined,
    fallback: string,
    ignoreCookie: boolean
  ): string {
    if (explicit) {
      return explicit;
    }

    if (!ignoreCookie && Cookie.has(key)) {
      return String(Cookie.get(key));
    }

    return fallback;
  }

  public setGid(gid: string): void {
    this.gid = gid;
  }

  public getPreviousGidFromUrl(): string {
    const gid = getUrlParameter(urlParameter.LEAD_GID);
    const isValid = this.isValidGid(gid);

    if (!isValid) {
      return '';
    }

    return gid;
  }

  public getSessionGidFromUrl(): string {
    const gid = getUrlParameter(urlParameter.SESSION_GID);
    const isValid = this.isValidGid(gid);

    if (!isValid) {
      return null;
    }

    return gid;
  }

  public getCustomerProfileGidFromUrl(): string {
    const gid = getUrlParameter(urlParameter.SFMC_SUBSCRIBER_KEY);
    const isValid = this.isValidGid(gid);

    if (!isValid) {
      return '';
    }

    return gid;
  }

  public isValidGid(gid: string, prefix?: string): boolean {
    if (!gid || typeof gid !== 'string') {
      return false;
    }

    const regexpPrefix = prefix || '[\\d]{3,}';
    const reactivationGidRegExp = new RegExp(`^${regexpPrefix}\\.[\\w-:]+$`, 'i');
    return reactivationGidRegExp.test(gid);
  }

  private isValidReactivationGid(gid: string): boolean {
    return this.isValidGid(gid, GID_REACTIVATION_PREFIX);
  }

  public getUrl({ encode }: { encode: boolean } = { encode: false }): string {
    const url = window.location.href;

    return encode ? encodeURIComponent(url) : url;
  }

  public getLocale(): string {
    return this.locale;
  }
}

/** Decoded, lower-case utm_source of a URL (query or fragment), or '' when absent. */
function getUtmSource(url: string): string {
  return decodeValue(parseUrlParameters(url).utm_source || '').toLowerCase();
}

/** The URL's click ID (the mapped `click_id` dimension). An unfilled macro (`{click_id}`) is not a click ID. */
function getClickId(url: string): string {
  const dimensions = mapTrackingDimensions(url);
  const errors = (dimensions[TRACKING_ERRORS_PROPERTY] as string[]) || [];
  const isMacro = errors.some((error) => error.startsWith('click_id:'));

  return dimensions.click_id && !isMacro ? String(dimensions.click_id) : '';
}

export default Session;
